import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as FileIcon,
  LocalShipping as ContainerIcon,
  Receipt as POIcon
} from '@mui/icons-material';
import { api } from '../config/api';
import ContainerNav from './ContainerNav';

interface ImportRecord {
  id: number | null;
  filename: string;
  import_date: string | null;
  import_type: 'Packing List' | 'Open Orders';
  lines_count: number;
  lines_matched: number | null;
  lines_unmatched: number | null;
  processing_status: string;
  supplier_name: string | null;
  po_number: string | null;
  po_count?: number;
  container_no: string | null;
  uploaded_by_staff_no: string | null;
  uploaded_by_name: string | null;
}

const ImportHistory: React.FC = () => {
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [totalPacking, setTotalPacking] = useState(0);
  const [totalOpenOrders, setTotalOpenOrders] = useState(0);

  useEffect(() => {
    loadImportHistory();
  }, []);

  const loadImportHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/import-history`);
      if (response.data.success) {
        setImports(response.data.imports || []);
        setTotalPacking(response.data.total_packing_lists || 0);
        setTotalOpenOrders(response.data.total_open_orders || 0);
      }
    } catch (err) {
      console.error('Error loading import history:', err);
      setError('Error loading import history');
    } finally {
      setLoading(false);
    }
  };

  // Filter imports
  const filteredImports = imports.filter(imp => {
    // Search filter
    if (appliedSearchTerm) {
      const search = appliedSearchTerm.toLowerCase();
      const matchesFilename = imp.filename?.toLowerCase().includes(search);
      const matchesPO = imp.po_number?.toLowerCase().includes(search);
      const matchesContainer = imp.container_no?.toLowerCase().includes(search);
      const matchesSupplier = imp.supplier_name?.toLowerCase().includes(search);
      const matchesUploadedBy = imp.uploaded_by_name?.toLowerCase().includes(search);
      if (!matchesFilename && !matchesPO && !matchesContainer && !matchesSupplier && !matchesUploadedBy) {
        return false;
      }
    }

    // Type filter
    if (typeFilter) {
      if (typeFilter === 'packing_list' && imp.import_type !== 'Packing List') return false;
      if (typeFilter === 'open_orders' && imp.import_type !== 'Open Orders') return false;
    }

    return true;
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'failed':
        return <Chip label="Failed" color="error" size="small" />;
      case 'processing':
        return <Chip label="Processing" color="warning" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'Packing List' ? <ContainerIcon fontSize="small" /> : <POIcon fontSize="small" />;
  };

  if (loading) {
    return (
      <Box p={3}>
        <ContainerNav />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <ContainerNav />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">{totalPacking + totalOpenOrders}</Typography>
              <Typography variant="body2" color="text.secondary">Total Imports</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">{totalPacking}</Typography>
              <Typography variant="body2" color="text.secondary">Packing Lists</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">{totalOpenOrders}</Typography>
              <Typography variant="body2" color="text.secondary">Open Orders Reports</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by filename, PO, container, supplier, or user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={() => setAppliedSearchTerm(searchTerm)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setAppliedSearchTerm(searchTerm);
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Import Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Import Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="packing_list">Packing Lists</MenuItem>
                  <MenuItem value="open_orders">Open Orders</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Imports Table */}
      {filteredImports.length === 0 ? (
        <Box textAlign="center" py={8}>
          <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No imports found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Import history will appear here once files are uploaded
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Import Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded By</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>PO / Container</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Lines</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredImports.map((imp, index) => (
                <TableRow
                  key={`${imp.import_type}-${imp.id || index}`}
                  sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  <TableCell>
                    <Chip
                      icon={getTypeIcon(imp.import_type)}
                      label={imp.import_type}
                      size="small"
                      variant="outlined"
                      color={imp.import_type === 'Packing List' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {imp.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(imp.import_date)}</TableCell>
                  <TableCell>
                    {imp.uploaded_by_name || '-'}
                  </TableCell>
                  <TableCell>{imp.supplier_name || '-'}</TableCell>
                  <TableCell>
                    {imp.import_type === 'Packing List' ? (
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {imp.po_number && (
                          <Chip label={`PO ${imp.po_number}`} size="small" color="primary" variant="outlined" />
                        )}
                        {imp.container_no && (
                          <Chip label={imp.container_no} size="small" variant="outlined" />
                        )}
                      </Box>
                    ) : (
                      imp.po_count ? `${imp.po_count} POs` : '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {imp.import_type === 'Packing List' ? (
                      <Box>
                        <Typography variant="body2">{imp.lines_count} total</Typography>
                        {imp.lines_matched !== null && (
                          <Typography variant="caption" color="text.secondary">
                            {imp.lines_matched} matched
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      imp.lines_count
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {getStatusChip(imp.processing_status)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ImportHistory;
